import React, { useState, useEffect } from 'react';
import { Menu, X, Globe } from 'lucide-react';
import { Button } from './ui/button';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';

const Header = () => {
  const { language, toggleLanguage } = useLanguage();
  const t = translations[language].nav;
  
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: t.home, href: '#hero' },
    { name: t.about, href: '#about' },
    { name: t.services, href: '#services' },
    { name: t.portfolio, href: '#portfolio' },
    { name: t.testimonials, href: '#testimonials' },
    { name: t.contact, href: '#contact' }
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-md shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <nav className="container mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a href="#hero" className="flex items-center space-x-3 group">
            <div className="text-2xl font-semibold text-[#8B7355] transition-colors duration-300">
              <span className="font-['Cormorant_Garamond'] tracking-wide">Komorebi Homes</span>
              <span className="ml-2 text-base font-light">木漏れ日</span>
            </div>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                className="text-sm font-light tracking-wide text-[#4a4a4a] hover:text-[#8B7355] transition-colors duration-300 relative group"
              >
                {link.name}
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#8B7355] transition-all duration-300 group-hover:w-full"></span>
              </a>
            ))}
            
            {/* Language Toggle */}
            <button
              onClick={toggleLanguage}
              className="flex items-center space-x-1 text-sm font-light text-[#4a4a4a] hover:text-[#8B7355] transition-colors duration-300"
              aria-label="Toggle language"
            >
              <Globe size={16} />
              <span>{language === 'en' ? '日本語' : 'EN'}</span>
            </button>
            
            <Button
              onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
              className="bg-[#8B7355] hover:bg-[#6d5a42] text-white px-6 py-2 rounded-none transition-all duration-300 hover:shadow-lg"
            >
              {t.getStarted}
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden text-[#4a4a4a] hover:text-[#8B7355] transition-colors duration-300"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden absolute top-20 left-0 right-0 bg-white shadow-lg py-6 px-6">
            <div className="flex flex-col space-y-4">
              {navLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.href}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="text-base text-[#4a4a4a] hover:text-[#8B7355] transition-colors duration-300 py-2 border-b border-gray-100"
                >
                  {link.name}
                </a>
              ))}
              
              {/* Language Toggle Mobile */}
              <button
                onClick={() => {
                  toggleLanguage();
                  setIsMobileMenuOpen(false);
                }}
                className="flex items-center space-x-2 text-base text-[#4a4a4a] hover:text-[#8B7355] transition-colors duration-300 py-2 border-b border-gray-100"
              >
                <Globe size={18} />
                <span>{language === 'en' ? '日本語に切り替え' : 'Switch to English'}</span>
              </button>
              
              <Button
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  document.getElementById('contact').scrollIntoView({ behavior: 'smooth' });
                }}
                className="bg-[#8B7355] hover:bg-[#6d5a42] text-white w-full mt-4 rounded-none"
              >
                {t.getStarted}
              </Button>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;
